import React, { useEffect } from 'react'
import { setConsent, useConsent } from '../lib/consent'

/**
 * The privacy policy, at /privacy.
 *
 * The website has its own copy (website/src/pages/Privacy.tsx), so each site
 * can show it without depending on the other. That copy is generated from this
 * file: after changing anything between the policy:start and policy:end
 * markers or the date, run `npm run sync-legal` in website/ (a website test
 * fails until you do). It renders without a session so it can be read before
 * signing up. Everything it says is a claim about how this codebase and its
 * deployment behave — change it in the same commit as anything that makes one
 * of those claims untrue (a new third party, a new cookie, analytics, a
 * different retention period).
 */

const LAST_UPDATED = '21 October 2026'
const CONTACT_EMAIL = 'privacy@sixpaths.co.uk'

function H2({ children }: { children: React.ReactNode }) {
  return <h2 className="font-display text-xl text-ink mt-10 mb-3">{children}</h2>
}

function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="font-sans font-medium text-ink mt-5 mb-2">{children}</h3>
}

function List({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-5 space-y-1.5 my-3">{children}</ul>
}

const Contact = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent underline hover:no-underline">
    {CONTACT_EMAIL}
  </a>
)

/** Shows the current cookie choice and lets it be changed, as easily as it was given. */
function ConsentControl() {
  const choice = useConsent()
  return (
    <div className="mt-4 p-4 bg-white border border-line rounded text-sm flex flex-col sm:flex-row sm:items-center gap-3">
      <p className="flex-1">
        {choice === 'accepted'
          ? 'You’ve accepted loading fonts from Google.'
          : choice === 'rejected'
            ? 'You’ve rejected loading fonts from Google.'
            : 'You haven’t made a choice yet, so nothing is loaded from Google.'}
      </p>
      {choice && (
        <button
          onClick={() => setConsent(choice === 'accepted' ? 'rejected' : 'accepted')}
          className="px-3 py-1.5 text-xs rounded-sm border border-line bg-white text-ink hover:bg-paper shrink-0"
        >
          {choice === 'accepted' ? 'Withdraw consent' : 'Accept instead'}
        </button>
      )}
    </div>
  )
}

export default function PrivacyPolicy() {
  useEffect(() => {
    const previous = document.title
    document.title = 'Privacy policy · Snapshot Money'
    return () => {
      document.title = previous
    }
  }, [])

  return (
    <div className="min-h-[100dvh] overflow-y-auto">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14 text-[15px] leading-relaxed text-ink/80">
        <a href="/" className="text-sm text-accent hover:underline">
          ← Back to Snapshot Money
        </a>

        <h1 className="font-display text-3xl sm:text-4xl text-ink mt-6">Privacy policy</h1>
        <p className="text-sm text-ink/70 mt-2">Last updated: {LAST_UPDATED}</p>

        {/* policy:start */}
        <p className="mt-6">
          This policy explains what personal data Snapshot Money collects when you use the app at
          app.snapshotmoney.co.uk and the website at snapshotmoney.co.uk (together, the “Service”), what
          we do with it, and the rights you have over it. It is written to meet the UK General Data
          Protection Regulation (UK GDPR) and the Data Protection Act 2018.
        </p>
        <p className="mt-3">
          The short version: we hold your email address, a scrambled form of your password, and the
          plan you type in. We use them to run the app for you. We don’t connect to your bank, we don’t
          run analytics or advertising, and we don’t sell or share your data for anyone else’s purposes.
        </p>
        <p className="mt-3">
          Questions: <Contact />.
        </p>

        <H2>1. Who we are</H2>
        <p>
          Snapshot Money is a trading name of <strong>Six Paths Software Ltd</strong>, a company
          registered in England and Wales (company number 11987916) with its registered office at 5
          Whitehall Walk, St. Neots, PE19 2EE. Six Paths Software Ltd is the “data controller” for the personal data
          described in this policy, which means we decide how and why it is used and we are responsible
          for looking after it.
        </p>

        <H2>2. What data we collect</H2>

        <H3>Account data</H3>
        <List>
          <li>Your email address.</li>
          <li>
            Your password, stored only as a one-way hash. We never see or store the password itself and
            cannot tell you what it is.
          </li>
          <li>Whether you have confirmed your email address, and when you created the account.</li>
          <li>
            Short-lived tokens for email confirmation and password reset links. Reset links expire
            after an hour.
          </li>
        </List>

        <H3>Your plan</H3>
        <p>Everything you enter into the app, which may include:</p>
        <List>
          <li>
            The accounts you set up — the names you give them and whether each counts towards your
            total. These are labels you choose; we don’t ask for account numbers or sort codes.
          </li>
          <li>
            For an account you have imported a statement into, an irreversible code made from the sort code
            and account number printed on the statement, so later statements can be matched to the right
            account. The numbers themselves are never stored.
          </li>
          <li>Income, fixed outgoings, budgets and one-off transactions, with their amounts and dates.</li>
          <li>Transfers between your accounts, with their amounts, dates and notes.</li>
          <li>Savings interest rates and the day each account pays interest.</li>
          <li>Starting balances, balance adjustments and the reasons you give for them.</li>
          <li>Loans and debts you flag, including interest rates and outstanding balances.</li>
          <li>Savings targets, what you actually spent against a budget, and your settings.</li>
          <li>
            Shop and payee names you have matched to a budget when importing a statement, so later imports
            can suggest the same budget.
          </li>
          <li>
            How much your imported statements showed you spent at each shop you matched to a budget or bill,
            per month, so you can compare it with the averages.
          </li>
          <li>Your sandbox — the separate “what if” copy of your plan.</li>
        </List>
        <p>
          You type this in yourself, or bring it in from a bank statement that you download from your bank
          and choose to import. Snapshot Money does not connect to your bank. An imported statement is read
          in your browser, and only the rows you choose to keep are saved — each one’s date, description
          and amount. Account numbers, sort codes, balances and the file’s other columns are not saved.
        </p>
        <p className="mt-3">
          Where a statement prints its sort code and account number, your browser sends just those two
          numbers to our server, which turns them into a code using a secret key only the server has, and
          sends the code back. The code is saved with that account in your plan, and the next statement’s
          code is compared with it, to warn you if a statement looks like it belongs to a different account.
          The code can’t be turned back into the numbers, even by someone with a copy of your plan, and the
          numbers are not stored or logged.
        </p>

        <H3>Shared merchant names</H3>
        <p>
          When you import a statement, the box <em>Share merchant names with Snapshot Money</em> is ticked
          unless you have unticked it before. While it is ticked, for each shop you match to a budget or
          bill we add its name — such as “Tesco” — to a list shared by everyone who uses the app, so that
          imports can show tidy names instead of the bank’s codes. Nothing else from your statement is
          sent: no amounts, dates, references, or payments to people.
        </p>
        <p className="mt-3">
          The list doesn’t say who shared a name. To count how many different people use one, we store
          with it a one-way code made from your account and that shop’s name. It can’t be turned back into
          your account, and it is different for every shop, so your shares can’t be linked to each other.
          Because we hold the key used to make it, the law still treats it as personal data, which is why
          it is described here and removed when you delete your account. A name is only shown to anyone
          else once at least ten different people have shared it.
        </p>

        <H3>Shared item names</H3>
        <p>
          When you add or rename an income, outgoing, budget or one-off, the box <em>Share what you call new items
          with Snapshot Money</em> is ticked unless you have unticked it before. While it is ticked, if the name you
          give the item isn’t one the app already suggests, we add that name — such as “Bristol Water” — with
          its type and category to a list used to improve the suggestions for everyone. Nothing else about the
          item is sent: no amounts, dates or notes. Names containing numbers or email addresses are never kept.
        </p>
        <p className="mt-3">
          As with merchant names, the list doesn’t say who shared a name: we store with it only a one-way code
          made from your account and that name, which is different for every name and removed when you delete
          your account. A name is never shown to anyone else automatically. Once at least ten different people
          have used it, one of the people who run Snapshot Money checks it, and only a name they approve
          becomes a suggestion. When a name is approved or turned down, the codes counting who used it are
          deleted.
        </p>

        <H3>Shared spending averages</H3>
        <p>
          When you import a statement you can tick <em>Also share how much I spend at these shops each month</em>.
          It is unticked unless you have ticked it before. While it is ticked, for each shop you match to a
          budget or bill whose name is already on the shared list, we receive, for each month in the statement,
          how many times you paid there and how much you spent in total, rounded to the nearest pound. Later
          statements for the same month add to it. We never receive single payments, their dates, or anything
          about one-offs. We never receive anything for shops that could reveal your health, religion,
          politics, trade union membership or sex life, such as pharmacies, clinics or places of worship.
        </p>
        <p className="mt-3">
          These figures are used only to work out anonymous averages: how much people typically spend at a shop
          or on a kind of spending such as groceries, how spending is split between shops, and how all of this
          changes from year to year. An average is only worked out when at least 25 people have contributed to
          it, and never when one person makes up a large share of it. Everyone signed in can see the averages.
        </p>
        <p className="mt-3">
          If you share, you also see how your own spending at each shop compares with its average. That
          comparison is worked out on your device from your own plan, which keeps what your imported statements
          showed you spent at each shop each month; none of your own figures are sent to make it. If you don’t
          share, or untick the box, you see the averages but not your own comparison.
        </p>
        <p className="mt-3">
          We store your figures with a one-way code instead of your account. The code is the same for all your
          shops in one month, so we can add up a month’s spending, and different for every month, so your
          months can’t be linked together. We keep your figures for up to 25 months, and delete them sooner if
          you untick the box, don’t open the app for 12 months, or delete your account. We also keep each
          month’s averages, which are worked out from at least 25 people and say nothing about any one of
          them, so they stay after your figures are deleted.
        </p>

        <H3>Technical data</H3>
        <List>
          <li>
            Your IP address, used briefly to limit repeated sign-up, sign-in and email requests (to stop
            abuse), and recorded in server error logs if something goes wrong while handling your
            request.
          </li>
          <li>
            A session cookie that keeps you signed in, and one that lets the app hear when you save from
            another device (see Section 10).
          </li>
          <li>The day you last opened the app, so shared spending totals can be deleted after a year away.</li>
          <li>
            Nothing from visiting the website: it sets no cookies and keeps no log of who visits or from
            which IP address.
          </li>
        </List>

        <H3>Messages you send us</H3>
        <p>If you email us, we keep the message and your address so we can reply.</p>

        <H3>What we don’t collect</H3>
        <p>
          We don’t use analytics, tracking pixels or advertising tools, and we don’t collect your name,
          phone number, location or device fingerprints. We don’t ask for special category data (such as
          health or religion) — please don’t put any in the free-text fields such as item names or
          adjustment reasons.
        </p>

        <H2>3. How we use your data</H2>
        <List>
          <li>To create your account, sign you in, and let you reset a forgotten password.</li>
          <li>
            To store your plan and show it back to you — the calendar, projections, budgets, targets and
            payoff calculator. The calculations run in your browser; the server stores your plan but does
            not analyse it.
          </li>
          <li>
            To send you service emails: confirming your address and resetting your password. We don’t
            send marketing emails.
          </li>
          <li>To keep the Service secure, stop abuse, and fix faults.</li>
          <li>To keep backups, so your plan can be recovered if something fails.</li>
          <li>
            To build the shared list of merchant names, if you leave sharing on, so statement imports can
            show clear names for everyone.
          </li>
          <li>
            To improve the names suggested when adding an item, from the names people give items, if you
            leave sharing on.
          </li>
          <li>To work out anonymous spending averages at shops, if you choose to share your monthly totals.</li>
          <li>To reply to you, and to meet our legal obligations.</li>
        </List>
        <p>
          We do not use your data for automated decision-making or profiling, and we never use your
          financial information to target advertising.
        </p>

        <H2>4. Our legal basis</H2>
        <List>
          <li>
            <strong>Contract</strong> — to provide the Service you have signed up for: your account, your
            stored plan, and the emails needed to operate your account.
          </li>
          <li>
            <strong>Legitimate interests</strong> — to keep the Service secure (rate limiting, error
            logs), to keep backups, to build the shared list of merchant names (only the names, only if
            you leave sharing on, and only shown once ten people share one), to improve name suggestions
            (only the names, only if you leave sharing on, and only once ten people use one and we have
            checked it), to work out anonymous spending averages (monthly totals only, only if you tick the
            box, never for sensitive shops, and only from 25 or more people), and to answer messages you
            send us. We have considered these against your rights and kept each to what is necessary. You
            can opt out of sharing merchant names at any time by unticking the box when you import, of
            sharing item names by unticking the box when you save an item, and of sharing spending totals
            by unticking that box when you import, which also deletes what you shared.
          </li>
          <li>
            <strong>Consent</strong> — to load our fonts from Google, only if you accept in the cookie
            banner. You can withdraw it at any time in Section 10.
          </li>
          <li>
            <strong>Legal obligation</strong> — where the law requires us to keep or disclose
            information.
          </li>
        </List>

        <H2>5. Who we share data with</H2>
        <p>
          We do not sell your personal data, and we do not share it with advertisers or data brokers.
          We use a small number of service providers (“processors”) who handle data on our instructions
          and under contracts requiring them to protect it:
        </p>
        <List>
          <li>
            <strong>Google Cloud</strong> — hosts the website, the app, its database and its backups, in Google’s
            London (europe-west2) region.
          </li>
          <li>
            <strong>Mailgun</strong> — delivers the confirmation and password reset emails, from its EU
            region. It receives your email address and the content of those emails.
          </li>
          <li>
            <strong>Google Fonts</strong> — only if you accept in the cookie banner, our typefaces are
            loaded from Google’s servers, so your browser sends Google its IP address and browser
            details when it fetches them. No cookies are set and no account data is sent. If you reject,
            nothing is fetched from Google and your device’s own fonts are used.
          </li>
        </List>
        <p>
          We may also disclose data to regulators, courts or law enforcement where the law requires it,
          or to a buyer or successor if the business is sold or restructured — in which case we would
          tell you before your data became subject to a different privacy policy.
        </p>

        <H2>6. International transfers</H2>
        <p>
          Your account data and plan are stored in the United Kingdom, and our email provider processes
          data in the European Union, which the UK recognises as providing adequate protection.
        </p>
        <p className="mt-3">
          Google and Mailgun are part of groups headquartered in the United States, and fonts (if you accept
          them) are served from Google’s global network, so some data may be accessible from or processed outside the UK
          and EU. Where that happens, the transfer is covered by UK adequacy regulations (including the
          UK Extension to the EU–US Data Privacy Framework) or by the UK International Data Transfer
          Addendum to the providers’ standard contractual clauses.
        </p>

        <H2>7. How long we keep your data</H2>
        <List>
          <li>
            <strong>Account and plan</strong> — for as long as your account exists. Deleting your account
            (under <em>Delete account</em> in the account menu) removes it and everything in it straight
            away; if you ask us to do it instead, we do so within one month.
          </li>
          <li>
            <strong>Backups</strong> — kept for up to 30 days, then deleted. Data you have deleted drops
            out of backups within that time.
          </li>
          <li>
            <strong>Shared merchant names</strong> — kept while they help with imports. The one-way codes
            counting who shared them are removed when you delete your account.
          </li>
          <li>
            <strong>Shared item names</strong> — the one-way codes counting who used a name are kept until
            the name is approved or turned down, or until you delete your account. An approved name stays as
            a suggestion while it is useful.
          </li>
          <li>
            <strong>Shared spending totals</strong> — up to 25 months, or sooner if you untick the box, don’t
            open the app for 12 months, or delete your account. Monthly averages, which describe no one person,
            are kept for year-on-year comparison.
          </li>
          <li>
            <strong>Sign-in sessions</strong> — end when you sign out, or after 30 days.
          </li>
          <li>
            <strong>Password reset and confirmation links</strong> — expire after use or after a short
            period; expired reset requests are removed.
          </li>
          <li>
            <strong>Rate-limiting records</strong> of your IP address — kept for no more than an hour.
          </li>
          <li>
            <strong>Server error logs</strong> — kept only briefly for troubleshooting, and cleared at
            the latest when the server is next updated or its size limit is reached.
          </li>
          <li>
            <strong>Emails you send us</strong> — for as long as needed to deal with your query, and no
            more than two years.
          </li>
        </List>

        <H2>8. How we protect your data</H2>
        <List>
          <li>All traffic between your browser and the Service is encrypted (HTTPS).</li>
          <li>Passwords are stored as salted one-way hashes.</li>
          <li>
            Data is encrypted at rest on Google Cloud’s disks and in backup storage, and the database is
            not reachable from the internet.
          </li>
          <li>
            Your session cookie can’t be read by scripts on the page, and sign-in attempts are rate
            limited.
          </li>
          <li>Access to the servers is restricted to the people who run the Service.</li>
        </List>
        <p>
          No system is perfectly secure. If a breach affecting your data occurs, we will notify you and
          the Information Commissioner’s Office where the law requires it.
        </p>

        <H2>9. Your rights</H2>
        <p>Under UK GDPR you have the right to:</p>
        <List>
          <li>
            <strong>Access</strong> the personal data we hold about you.
          </li>
          <li>
            <strong>Rectify</strong> data that is wrong — most of it you can edit directly in the app.
          </li>
          <li>
            <strong>Erase</strong> your data (the “right to be forgotten”).
          </li>
          <li>
            <strong>Restrict</strong> or <strong>object to</strong> how we use it.
          </li>
          <li>
            <strong>Data portability</strong> — everything we hold about you (your email address, when
            you signed up, your plan and your sandbox) can be downloaded at any time as a JSON file from{' '}
            <em>Export data</em> in the account menu, or we can send it to you.
          </li>
        </List>
        <p>
          You can export your data and delete your account yourself from the account menu. To exercise
          any of these rights another way, email <Contact /> from the address on your account. We will
          respond within one month.
        </p>
        <p className="mt-3">
          If you’re unhappy with how we have handled your data, you can complain to the Information
          Commissioner’s Office at{' '}
          <a href="https://ico.org.uk/make-a-complaint/" className="text-accent underline hover:no-underline">
            ico.org.uk
          </a>
          . We would appreciate the chance to put things right first.
        </p>

        <H2>10. Cookies and browser storage</H2>
        <p>
          The website uses no cookies. The app uses two, both strictly necessary for it to work, so they
          don’t need your consent:
        </p>
        <List>
          <li>
            A <strong>session cookie</strong> that keeps you signed in. It lasts up to 30 days and is
            removed when you sign out.
          </li>
          <li>
            A <strong>live-updates cookie</strong>, so that when you save on one device your other open
            tabs and devices update straight away. It holds a signed token that lets this browser listen
            for changes to your own plan, and nothing else. It is only sent to the part of the Service
            that delivers those updates, and lasts up to 12 hours.
          </li>
        </List>
        <p className="mt-3">
          Neither can be read by scripts on the page, and neither is used for tracking.
        </p>
        <p className="mt-3">
          The app also uses your browser’s local storage to keep a copy of your plan on your device, so
          it still opens without a signal, to reopen on the account and view you last had on screen, and
          to remember your cookie choice and that you’ve dismissed the introductory tour or the offer to
          install the app. The copy of your plan is removed when you sign out; the rest stays on your
          device until you clear your browser’s site data. None of it is sent to us or used for
          tracking.
        </p>
        <p className="mt-3">
          The website uses local storage only to remember your cookie choice, and keeps it on your device.
        </p>
        <p className="mt-3">
          The one optional extra is loading our fonts from Google (see Section 5), which we only do if
          you accept in the cookie banner. You can change your mind at any time. The website and the app
          each ask separately, because each remembers its own answer; the control below changes it for
          the one you’re reading this on.
        </p>
        <ConsentControl />

        <H2>11. Children</H2>
        <p>
          The Service is intended for people aged 18 and over. We don’t knowingly collect data from
          children; if you believe a child has created an account, contact us and we will delete it.
        </p>

        <H2>12. Changes to this policy</H2>
        <p>
          We will update this policy if what we collect or how we use it changes. If a change is
          significant — for example, a new kind of data or a new provider — we will tell you by email or
          in the app before it takes effect. The date at the top shows when it last changed.
        </p>

        <H2>13. Contact us</H2>
        <p>
          Six Paths Software Ltd, trading as Snapshot Money
          <br />
          Registered office: 5 Whitehall Walk, St. Neots, PE19 2EE
          <br />
          Company number 11987916, registered in England and Wales
          <br />
          Email: <Contact />
        </p>
        {/* policy:end */}

        <p className="mt-12 pt-6 border-t border-line text-sm">
          <a href="/" className="text-accent hover:underline">
            ← Back to Snapshot Money
          </a>
        </p>
      </div>
    </div>
  )
}
